import type { ReactNode } from 'react';
import { isAtLeast, type CoachLevel } from '@4cm/core/profiles';
import { useCalibration } from '../../contexts/CalibrationContext';

/**
 * Conditional wrapper that renders children only if the coach's level
 * meets the floor (`min`) and / or ceiling (`max`).
 *
 * The brief's principle #4 ("Calibrate to coach experience level") wants
 * every surface to flex. Web2 had only a binary novice/academy toggle
 * applied in 6/26 pages. This primitive — paired with the 5-tier
 * `coach_level` already on the users table (NOVICE → GRASSROOTS →
 * COMPETITIVE → ADVANCED → ELITE_CAT1) — lets every page hide / surface
 * sections without per-component state plumbing.
 *
 * Patterns:
 *  - hide novice-only welcome banner from advanced coaches:
 *      <Calibrated max="GRASSROOTS">{...welcome banner...}</Calibrated>
 *  - reveal a 3D import button from competitive up:
 *      <Calibrated min="COMPETITIVE"><OpenIn3D/></Calibrated>
 *  - elite-only deep-analytics panel:
 *      <Calibrated min="ELITE_CAT1">{...periodisation chart...}</Calibrated>
 *
 * Defaults match the AGENTS.md "hide unavailable features, don't disable
 * them" rule — when the coach's level doesn't meet `min`, children are
 * not rendered. No greyed-out buttons.
 */
interface CalibratedProps {
  children: ReactNode;
  /** Minimum coach level required to render. Inclusive. */
  min?: CoachLevel;
  /** Maximum coach level. Inclusive — useful for novice-only nudges. */
  max?: CoachLevel;
  /** Render this instead of nothing when the coach's level is outside
   *  the band. Use sparingly — most cases want a clean hide. */
  fallback?: ReactNode;
}

const LEVEL_ORDER: CoachLevel[] = ['NOVICE', 'GRASSROOTS', 'COMPETITIVE', 'ADVANCED', 'ELITE_CAT1'];

function isAtMost(max: CoachLevel, have: CoachLevel | null | undefined): boolean {
  if (!have) return true;
  return LEVEL_ORDER.indexOf(have) <= LEVEL_ORDER.indexOf(max);
}

export function Calibrated({ children, min, max, fallback = null }: CalibratedProps) {
  const { coachLevel } = useCalibration();
  const meetsFloor = !min || isAtLeast(min, coachLevel);
  const meetsCeiling = !max || isAtMost(max, coachLevel);
  if (meetsFloor && meetsCeiling) return <>{children}</>;
  return <>{fallback}</>;
}

/** Hook-shaped variant for in-line conditionals (e.g. computing
 *  array contents that need calibration filtering before render). */
export function useCalibrated(min?: CoachLevel, max?: CoachLevel): boolean {
  const { coachLevel } = useCalibration();
  const meetsFloor = !min || isAtLeast(min, coachLevel);
  const meetsCeiling = !max || isAtMost(max, coachLevel);
  return meetsFloor && meetsCeiling;
}
